import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from './lib/api'
import {
  fetchLocations, fetchCandidates, fetchActiveCandidates,
  fetchActivityLog, addActivityLog,
  addLocation, updateLocation, deleteLocation,
  addCandidate, updateCandidate, deleteCandidate,
  uploadPhoto, subscribeToChanges, unsubscribe,
  fetchAgents, getUserProfile, fetchRequests,
} from './lib/api'
import Board from './components/Board'
import MapView from './components/MapView'
import CandidateModal from './components/CandidateModal'
import LocationModal from './components/LocationModal'
import DetailModal from './components/DetailModal'
import CitiesPage from './components/CitiesPage'
import CalendarView from './components/CalendarView'
import AnalyticsPage from './components/AnalyticsPage'
import AgentsPage from './components/AgentsPage'
import ProfilePage from './components/ProfilePage'
import RequestsPage from './components/RequestsPage'
import GeoTracker from './components/GeoTracker'
import SpecialBadge, { IS_SPECIAL } from './components/SpecialBadge'
import './App.css'

const STATUS_COLORS = {
  'прибыл': '#639922',
  'в пути': '#EF9F27',
  'ожидает': '#378ADD',
  'завершен': '#9B59B6',
}

async function autoMoveCandidates(candidates, locations, ensureLocation) {
  const now = new Date()
  const updated = []
  for (const c of candidates) {
    if (c.archived || !Array.isArray(c.route) || c.route.length === 0) { updated.push(c); continue }
    const lastPoint = c.route[c.route.length - 1]
    if (!lastPoint.to_date) { updated.push(c); continue }
    try {
      const timeStr = lastPoint.to_time ? lastPoint.to_time.slice(0, 5) : '00:00'
      const [y, m, d] = lastPoint.to_date.split('-').map(Number)
      const [h, min] = timeStr.split(':').map(Number)
      const arrivalUtc = new Date(Date.UTC(y, m - 1, d, h, min))
      const tz = lastPoint.to_tz || 'Europe/Moscow'
      const offsetStr = new Intl.DateTimeFormat('en', { timeZone: tz, timeZoneName: 'shortOffset' }).formatToParts(arrivalUtc).find(p => p.type === 'timeZoneName')?.value || 'UTC+0'
      const match = offsetStr.match(/UTC([+-])(\d+)(?::(\d+))?/)
      const offsetMin = match ? (match[1] === '-' ? -1 : 1) * (parseInt(match[2]) * 60 + (parseInt(match[3]) || 0)) : 0
      const arrivalActual = new Date(arrivalUtc.getTime() - offsetMin * 60000)

      if (arrivalActual <= now) {
        let targetLoc = locations.find(l => l.name === lastPoint.to)
        if (!targetLoc) {
          targetLoc = await ensureLocation({ name: lastPoint.to, icon: '📍', timezone: tz, sort_order: locations.length })
        }
        if (targetLoc && c.location_id !== targetLoc.id) {
          await updateCandidate(c.id, { location_id: targetLoc.id })
          updated.push({ ...c, location_id: targetLoc.id })
          continue
        }
      }
    } catch (e) { console.error('autoMove error for', c.name, e) }
    updated.push(c)
  }
  return updated
}

function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e) {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password })
      if (error) {
        setError('Неверный email или пароль')
      } else if (data?.session) {
        onLogin(data.session)
      }
    } catch (e) {
      setError('Ошибка: ' + (e.message || e))
    }
    setLoading(false)
  }

  if (IS_SPECIAL) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #0a0a0f 0%, #12101a 100%)' }}>
      <div style={{ width: 340 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'linear-gradient(135deg, #7c5fe6, #a855f7)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 24 }}>🔒</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#fff', letterSpacing: '-0.3px' }}>Закрытый доступ</h1>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', marginTop: 6 }}>Авторизация</p>
        </div>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" required style={{ padding: '12px 16px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.1)', fontSize: 14, background: 'rgba(255,255,255,0.06)', color: '#fff', outline: 'none' }} />
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Пароль" required style={{ padding: '12px 16px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.1)', fontSize: 14, background: 'rgba(255,255,255,0.06)', color: '#fff', outline: 'none' }} />
          {error && <div style={{ fontSize: 12, color: '#f87171', background: 'rgba(248,113,113,0.1)', padding: '8px 12px', borderRadius: 8 }}>{error}</div>}
          <button type="submit" disabled={loading} style={{ marginTop: 4, padding: '12px', borderRadius: 10, background: 'linear-gradient(135deg, #7c5fe6, #a855f7)', color: 'white', border: 'none', fontSize: 14, fontWeight: 600, cursor: 'pointer', letterSpacing: '0.2px' }}>
            {loading ? 'Вход...' : 'Войти'}
          </button>
        </form>
      </div>
    </div>
  )

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f3' }}>
      <div style={{ background: 'white', border: '1px solid #e8e8e5', borderRadius: 14, padding: '40px 36px', width: 340 }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📍</div>
          <h1 style={{ fontSize: 20, fontWeight: 600, color: '#1a1a1a' }}>Трекер перемещений</h1>
          <p style={{ fontSize: 13, color: '#888', marginTop: 6 }}>Войдите чтобы продолжить</p>
        </div>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, color: '#888', fontWeight: 500 }}>Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" required style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e0e0dc', fontSize: 14 }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, color: '#888', fontWeight: 500 }}>Пароль</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e0e0dc', fontSize: 14 }} />
          </div>
          {error && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 8 }}>{error}</div>}
          <button type="submit" disabled={loading} style={{ marginTop: 4, padding: '10px', borderRadius: 8, background: '#1a1a1a', color: 'white', border: 'none', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
            {loading ? 'Вход...' : 'Войти'}
          </button>
        </form>
      </div>
    </div>
  )
}

function GeoPage() {
  const token = window.location.pathname.replace('/geo/', '')
  return <GeoTracker token={token} />
}

export default function App() {
  if (window.location.pathname.startsWith('/geo/')) return <GeoPage />
  const [session, setSession] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [authLoading, setAuthLoading] = useState(true)
  const [tab, setTabRaw] = useState(() => {
    const h = localStorage.getItem('nav') || ''
    return ['board','map','calendar'].includes(h) ? h : 'board'
  })
  const [showProfile, setShowProfileRaw] = useState(() => localStorage.getItem('nav') === 'profile')
  const [showRequests, setShowRequestsRaw] = useState(() => localStorage.getItem('nav') === 'requests')
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0)
  const [locations, setLocations] = useState([])
  const [candidates, setCandidates] = useState([])
  const [search, setSearch] = useState('')
const [flagFilter, setFlagFilter] = useState('')
const [archiveResponsibleFilter, setArchiveResponsibleFilter] = useState('')
const [responsibleFilter, setResponsibleFilter] = useState('')
  const [loading, setLoading] = useState(true)
  const [candidateModal, setCandidateModal] = useState(null)
  const [locationModal, setLocationModal] = useState(null)
  const [detailModal, setDetailModal] = useState(null)
  const [detailModalTab, setDetailModalTab] = useState('details')
const [backToFlightCard, setBackToFlightCard] = useState(null)
const [reopenFlightCard, setReopenFlightCard] = useState(null)
  const [showCities, setShowCitiesRaw] = useState(() => localStorage.getItem('nav') === 'cities')
const [showAnalytics, setShowAnalyticsRaw] = useState(() => localStorage.getItem('nav') === 'analytics')
  const [analyticsFrom, setAnalyticsFrom] = useState(null)
const [showAgents, setShowAgentsRaw] = useState(() => localStorage.getItem('nav') === 'agents')
  const [openAgentId, setOpenAgentId] = useState(null)
  const [agents, setAgents] = useState([])
  const [log, setLog] = useState([])
const [showLog, setShowLog] = useState(false)
const WORKSPACE = process.env.REACT_APP_WORKSPACE || 'main'
const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true')
const [workspace] = useState(WORKSPACE)

  useEffect(() => {
    document.body.classList.toggle('dark', darkMode)
    localStorage.setItem('darkMode', darkMode)
  }, [darkMode])

  function saveNav(h) { localStorage.setItem('nav', h) }
  function setTab(v) { setTabRaw(v); saveNav(v) }
  function setShowProfile(v) { setShowProfileRaw(v); saveNav(v ? 'profile' : tab) }
  function setShowRequests(v) { setShowRequestsRaw(v); saveNav(v ? 'requests' : tab) }
  function setShowCities(v) { setShowCitiesRaw(v); saveNav(v ? 'cities' : tab) }
  function setShowAnalytics(v) { setShowAnalyticsRaw(v); saveNav(v ? 'analytics' : tab) }
  function setShowAgents(v) { setShowAgentsRaw(v); saveNav(v ? 'agents' : tab) }

const role = userProfile?.role || 'viewer'
  const isAdmin = role === 'admin'
  const isOperator = role === 'operator'
  const isViewer = role === 'viewer'
  const isExternal = role === 'external'
  const isMoscowOperator = role === 'moscow_operator'

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session)
      if (session) {
        const profile = await getUserProfile(session.user.id)
        if (profile) setUserProfile(profile)
      }
      setAuthLoading(false)
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session)
      if (session) {
        const profile = await getUserProfile(session.user.id)
        if (profile) setUserProfile(profile)
      } else {
        setUserProfile(null)
      }
    })
    return () => subscription.unsubscribe()
  }, [])

const addLog = useCallback(async (msg, user) => {
    const now = new Date()
    const date = now.toLocaleDateString('ru-RU', { timeZone: 'Europe/Moscow', day: '2-digit', month: '2-digit', year: 'numeric' })
    const time = now.toLocaleTimeString('ru-RU', { timeZone: 'Europe/Moscow', hour: '2-digit', minute: '2-digit' })
    const operator = user || ''
    setLog(prev => [{ msg, time, date, operator }, ...prev].slice(0, 50))
    try {
      await addActivityLog(msg, operator, workspace)
    } catch (e) { console.error(e) }
  }, [])

useEffect(() => {
    if (!session) return
    async function load() {
      try {
        const [locs, cands, logs, agts] = await Promise.all([fetchLocations(), fetchActiveCandidates(workspace), fetchActivityLog(workspace), fetchAgents()])
        setAgents(agts || [])
        let finalLocs = locs
        const updatedCands = cands
        setLocations(finalLocs)
        setCandidates(updatedCands)
   
        setLog(logs.map(l => {
          const dt = new Date(l.created_at)
          return {
            msg: l.message,
            operator: l.operator || '',
            date: dt.toLocaleDateString('ru-RU', { timeZone: 'Europe/Moscow', day: '2-digit', month: '2-digit', year: 'numeric' }),
            time: dt.toLocaleTimeString('ru-RU', { timeZone: 'Europe/Moscow', hour: '2-digit', minute: '2-digit' }),
          }
        }))
      } catch (e) {
        console.error(e)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [session])

  useEffect(() => {
    if (!session) return
    const channel = subscribeToChanges(
      () => { fetchActiveCandidates(workspace).then(c => { if (c) setCandidates(c) }).catch(() => {}) },
      () => { fetchLocations().then(l => { if (l) setLocations(l) }).catch(() => {}) }
    )
    return () => unsubscribe(channel)
  }, [session])

  useEffect(() => {
    if (!session) return
    const loadCount = async () => {
      try {
        const reqs = await fetchRequests(workspace)
        setPendingRequestsCount((reqs || []).filter(r => r.status === 'pending').length)
      } catch { setPendingRequestsCount(0) }
    }
    loadCount()
    const interval = setInterval(loadCount, 180000)
    return () => clearInterval(interval)
  }, [session])

  async function handleSaveCandidate(data, photoFile) {
    try {
      if (candidateModal.mode === 'add') {
        const created = await addCandidate({ ...data, workspace })
        if (photoFile) {
          const url = await uploadPhoto(photoFile, created.id)
          await updateCandidate(created.id, { photo_url: url })
        }
        addLog('Добавлен: ' + data.name, userProfile?.full_name)
      } else {
        await updateCandidate(candidateModal.data.id, data)
        if (photoFile) {
          const url = await uploadPhoto(photoFile, candidateModal.data.id)
          await updateCandidate(candidateModal.data.id, { photo_url: url })
        }
        addLog('Обновлён: ' + data.name, userProfile?.full_name)
      }
      setCandidateModal(null)
    } catch (e) { alert('Ошибка: ' + e.message) }
  }

async function handleArchiveCandidate(id) {
    const c = candidates.find(x => x.id === id) || detailModal
    if (!c) return
    if (c.archived) {
      try {
        await updateCandidate(id, { archived: false, archive_reason: null })
        addLog('Восстановлен: ' + c.name, userProfile?.full_name)
        setDetailModal(null)
      } catch (e) { alert('Ошибка: ' + e.message) }
    } else {
      const reason = window.prompt('Укажите причину архивирования:')
      if (reason === null || reason.trim() === '') {
        if (reason !== null) alert('Причина архивирования обязательна')
        return
      }
      const archiveDate = new Date().toLocaleDateString('ru-RU', { timeZone: 'Europe/Moscow', day: '2-digit', month: '2-digit', year: 'numeric' })
      try {
        await updateCandidate(id, { archived: true, archive_reason: reason.trim() + ' (' + archiveDate + ')' })
        addLog('Архивирован: ' + c.name + ' — ' + reason.trim(), userProfile?.full_name)
        setDetailModal(null)
      } catch (e) { alert('Ошибка: ' + e.message) }
    }
  }
async function handleDeleteCandidate(id) {
    const c = candidates.find(x => x.id === id)
    if (!window.confirm('Удалить "' + c?.name + '"?')) return
    try {
      await deleteCandidate(id)
      addLog('Удалён: ' + c?.name, userProfile?.full_name)
      setCandidates(prev => prev.filter(x => x.id !== id))
      setDetailModal(null)
    } catch (e) { alert('Ошибка: ' + e.message) }
  }

  async function handleMoveCandidate(candidateId, newLocId) {
    const c = candidates.find(x => x.id === candidateId)
    if (isViewer) return
    if (isExternal && c?.responsible_id !== session?.user?.id) {
      alert('Вы можете перемещать только своих кандидатов')
      return
    }
    if (isOperator && c?.responsible_id !== session?.user?.id) {
      alert('Вы можете перемещать только своих кандидатов')
      return
    }
    const fromLoc = locations.find(l => l.id === c?.location_id)
    const toLoc = locations.find(l => l.id === newLocId)
    try {
      await updateCandidate(candidateId, { location_id: newLocId })
      addLog(c?.name + ': ' + (fromLoc?.name || '?') + ' → ' + (toLoc?.name || '?'), userProfile?.full_name)
    } catch (e) { alert('Ошибка: ' + e.message) }
  }

  async function handleSaveLocation(data) {
    try {
      if (locationModal.mode === 'add') {
        await addLocation(data)
        addLog('Добавлена локация: ' + data.name, userProfile?.full_name)
      } else {
        await updateLocation(locationModal.data.id, data)
        setLocations(prev => prev.map(l => l.id === locationModal.data.id ? { ...l, ...data } : l))
        addLog('Обновлена локация: ' + data.name, userProfile?.full_name)
      }
      setLocationModal(null)
    } catch (e) { alert('Ошибка: ' + e.message) }
  }
async function handleReorderLocations(newOrder) {
    setLocations(newOrder)
    try {
      await Promise.all(newOrder.map((loc, index) =>
        updateLocation(loc.id, { sort_order: index })
      ))
    } catch (e) { console.error(e) }
  }
  async function handleDeleteLocation(id) {
    const loc = locations.find(l => l.id === id)
    if (candidates.some(c => c.location_id === id && !c.is_agent) && !window.confirm('В "' + loc?.name + '" есть кандидаты. Удалить?')) return
    try {
      await deleteLocation(id)
      setLocationModal(null)
    } catch (e) { alert('Ошибка: ' + e.message) }
  }

  async function handleLogout() {
    await supabase.auth.signOut()
    setSession(null)
    setUserProfile(null)
  }

  if (authLoading) return <div className="loading-screen"><div className="loading-spinner"></div></div>
  if (!session) return <LoginScreen onLogin={async (session) => {
    setSession(session)
    const profile = await getUserProfile(session.user.id)
    if (profile) setUserProfile(profile)
  }} />
  if (showCities) return <div className={darkMode ? 'dm-page' : ''} style={{ background: darkMode ? '#0f1117' : undefined, minHeight: '100vh' }}><CitiesPage onClose={() => setShowCities(false)} darkMode={darkMode} /></div>
if (showAnalytics) return <div className={darkMode ? 'dm-page' : ''} style={{ background: darkMode ? '#0f1117' : undefined, minHeight: '100vh' }}><AnalyticsPage candidates={candidates} locations={locations} onClose={() => { setShowAnalytics(false); if (analyticsFrom === 'profile') setShowProfile(true); setAnalyticsFrom(null) }} isAdmin={isAdmin} workspace={workspace} /></div>
if (showAgents) return <div className={darkMode ? 'dm-page' : ''} style={{ background: darkMode ? '#0f1117' : undefined, minHeight: '100vh' }}><AgentsPage candidates={candidates} onClose={() => { setShowAgents(false); setOpenAgentId(null) }} canAddAgent={isAdmin || isOperator} isAdmin={isAdmin} initialAgentId={openAgentId} /></div>
if (showRequests) return <div className={darkMode ? 'dm-page' : ''} style={{ background: darkMode ? '#0f1117' : undefined, minHeight: '100vh' }}><RequestsPage onClose={() => setShowRequests(false)} isAdmin={isAdmin} darkMode={darkMode} currentUserName={userProfile?.full_name || ''} /></div>

const filteredCandidates = candidates.filter(c => {
    if (c.moscow_completed) return false
    if ((isExternal || isOperator) && c.responsible_id !== session?.user?.id) return false
    if (flagFilter && c.flag !== flagFilter) return false
    if (responsibleFilter && c.responsible_name !== responsibleFilter) return false
    if (!search) return true
    const q = search.toLowerCase()
    const visaMatch = (c.visas || []).some(v => v.application_number?.toLowerCase().includes(q))
    return c.name?.toLowerCase().includes(q) || c.citizenship?.toLowerCase().includes(q) || c.passport?.toLowerCase().includes(q) || c.phone?.includes(q) || c.city?.toLowerCase().includes(q) || visaMatch
  })

  const activeCandidates = filteredCandidates.filter(c => !c.archived)
  const archivedCandidates = filteredCandidates.filter(c => c.archived && (!archiveResponsibleFilter || c.responsible_name === archiveResponsibleFilter))
  const activeCandidatesAll = (isOperator || isExternal)
    ? activeCandidates
    : candidates.filter(c => !c.archived && !c.moscow_completed)
  const stats = {
    total: activeCandidatesAll.length,
    arrived: activeCandidatesAll.filter(c => c.status === 'прибыл').length,
    transit: activeCandidatesAll.filter(c => c.status === 'в пути').length,
    waiting: activeCandidatesAll.filter(c => c.status === 'ожидает').length,
  }

  if (loading) return <div className="loading-screen"><div className="loading-spinner"></div><p>Загрузка...</p></div>

  return (
    <div className={darkMode ? 'app dm-page' : 'app'}>
      <header className="header">
        <div className="header-inner">
          <div className="header-left">
            <span className="logo">📍</span>
            <h1>Трекер перемещений</h1>
            <SpecialBadge />
          </div>
          <div className="header-actions">
            <span style={{ fontSize: 13, color: '#888', marginRight: 8 }}>{userProfile?.full_name || session.user.email}</span>
            <button className="btn btn-outline" onClick={() => setShowProfile(true)}>👤 Мой кабинет</button>
            {isAdmin && <button className="btn btn-outline" onClick={() => setShowCities(true)}>🌍 Города</button>}{(isAdmin || isViewer) && <button className="btn btn-outline" onClick={() => setShowAnalytics(true)}>📊 Аналитика</button>}
{(isAdmin || isViewer || isOperator) && <button className="btn btn-outline" onClick={() => setShowAgents(true)}>🤝 Агенты</button>}
            {!IS_SPECIAL && <button className="btn btn-outline" onClick={() => setShowRequests(true)} style={{ position: 'relative' }}>
              📬 Заявки
              {pendingRequestsCount > 0 && (
                <span style={{ position: 'absolute', top: -6, right: -6, background: '#E24B4A', color: 'white', borderRadius: '50%', fontSize: 10, fontWeight: 700, minWidth: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px' }}>
                  {pendingRequestsCount}
                </span>
              )}
            </button>}
            <button className="btn btn-outline" onClick={() => setShowLog(true)}>📋 Журнал</button>
            {!isViewer && !isMoscowOperator && <button className="btn btn-outline" onClick={() => setLocationModal({ mode: 'add', data: null })}>+ Локация</button>}
            <button className="btn btn-outline" onClick={() => setDarkMode(d => !d)} title="Тёмная тема">{darkMode ? '☀️' : '🌙'}</button>
            <button className="btn btn-outline" onClick={handleLogout}>Выйти</button>

          </div>
        </div>
      </header>

      <main className="main">
        <div className="stats-row">
          <div className="stat-card"><span className="stat-val">{stats.total}</span><span className="stat-lbl">Всего</span></div>
          <div className="stat-card arrived"><span className="stat-val">{stats.arrived}</span><span className="stat-lbl">Прибыли</span></div>
          <div className="stat-card transit"><span className="stat-val">{stats.transit}</span><span className="stat-lbl">В пути</span></div>
          <div className="stat-card waiting"><span className="stat-val">{stats.waiting}</span><span className="stat-lbl">Ожидают</span></div>
          <div className="stat-card"><span className="stat-val">{locations.length}</span><span className="stat-lbl">Локаций</span></div>
        </div>

        <div className="toolbar">
          <div className="tabs">
            <button className={'tab ' + (tab === 'board' ? 'active' : '')} onClick={() => setTab('board')}>🗂 Доска</button>
            <button className={'tab ' + (tab === 'map' ? 'active' : '')} onClick={() => setTab('map')}>🗺 Карта</button>
<button className={'tab ' + (tab === 'calendar' ? 'active' : '')} onClick={() => setTab('calendar')}>📅 Календарь</button>
          </div>
{isViewer && (
            <select value={responsibleFilter} onChange={e => setResponsibleFilter(e.target.value)} style={{ fontSize: 13, padding: '6px 10px', borderRadius: 8, border: '1px solid #e0e0dc', background: 'white' }}>
              <option value="">Все ответственные</option>
              {[...new Set(candidates.filter(c => !c.archived && !c.moscow_completed && c.responsible_name).map(c => c.responsible_name))].sort().map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          )}
          <select value={flagFilter} onChange={e => setFlagFilter(e.target.value)} style={{ fontSize: 13, padding: '6px 10px', borderRadius: 8, border: '1px solid #e0e0dc', background: 'white' }}>
            <option value="">Все флаги</option>
            <option value="🔴 Срочно">🔴 Срочно</option>
            <option value="🟡 Ожидание">🟡 Ожидание</option>
            <option value="🟢 Готов">🟢 Готов</option>
            <option value="🔵 VIP">🔵 VIP</option>
          </select>
          <div className="search-wrap">
            <span className="search-icon">🔍</span>
            <input className="search-input" type="text" placeholder="Поиск..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>

{tab === 'board' && (<>
          <Board
            locations={locations}
            candidates={activeCandidates}
            statusColors={STATUS_COLORS}
            onMoveCandidate={handleMoveCandidate}
           
            onEditLocation={(loc) => setLocationModal({ mode: 'edit', data: loc })}
            onAddLocation={(region) => setLocationModal({ mode: 'add', data: { region: region || 'АФРИКА' } })}
onReorderLocations={handleReorderLocations}
            onClickCandidate={(c) => setDetailModal(c)}
            isViewer={isViewer || isMoscowOperator}
            isAdmin={isAdmin}
            currentUserId={session?.user?.id}
          />
           
       </>)}
{tab === 'map' && (
          <MapView
            locations={locations}
            candidates={activeCandidates}
            statusColors={STATUS_COLORS}
            onClickCandidate={(c) => setDetailModal(c)}
          />
        )}
        {tab === 'calendar' && (
          <CalendarView
            candidates={activeCandidates}
            agents={agents}
            locations={locations}
            onClickCandidate={(c) => setDetailModal(c)}
            onClickFromFlight={(c, ticketId) => { setBackToFlightCard({ ticketId, candidate: c }); setDetailModal(c) }}
            onClickAgent={(agentId) => { setOpenAgentId(agentId || null); setShowAgents(true) }}
            currentUserId={session?.user?.id}
           currentUserName={userProfile?.full_name || ''}
          isAdmin={isAdmin}
          />
        )}


      </main>

      {candidateModal && <CandidateModal mode={candidateModal.mode} initialData={candidateModal.data} locations={locations} onSave={handleSaveCandidate} onClose={() => setCandidateModal(null)} isAdmin={isAdmin} currentUserId={userProfile?.id} currentUserName={userProfile?.full_name} isOperator={isOperator} />}
      {locationModal && <LocationModal mode={locationModal.mode} initialData={locationModal.data} onSave={handleSaveLocation} onDelete={handleDeleteLocation} onClose={() => setLocationModal(null)} />}

{showLog && (
        <div className="modal-backdrop" onClick={() => setShowLog(false)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📋 Журнал действий</h2>
              <button className="close-btn" onClick={() => setShowLog(false)}>✕</button>
            </div>
            <div className="detail-body">
              {log.length === 0 && <div style={{ fontSize: 13, color: '#999', textAlign: 'center', padding: 20 }}>Нет действий</div>}
              {log.map((l, i) => (
                <div key={i} className="log-item" style={{ borderBottom: '1px solid #f5f5f3', padding: '8px 0' }}>
                  <span className="log-msg">{l.msg}</span>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    {l.operator && <span style={{ fontSize: 11, color: '#378ADD' }}>{l.operator}</span>}
                    <span className="log-time">{l.date} {l.time} МСК</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
{showProfile && (
        <ProfilePage
          darkMode={darkMode}
          session={session}
          userProfile={userProfile}
          isAdmin={isAdmin}
          isOperator={isOperator}
          isViewer={isViewer}
          isMoscowOperator={isMoscowOperator}
          candidates={candidates}
          locations={locations}
          onClose={() => setShowProfile(false)}
          workspace={workspace}
          onRefresh={async () => {
            const fresh = await fetchActiveCandidates(workspace)
            setCandidates(fresh)
          }}
         onOpenCandidate={(c, tab) => { setDetailModal(c); if (tab) setDetailModalTab(tab) }}
          onEditCandidate={(c) => setCandidateModal({ mode: 'edit', data: c })}
onAddCandidate={() => setCandidateModal({ mode: 'add', data: null })}
          onAddTrip={(c) => setDetailModal(c)}
          onAddExpense={(c) => setDetailModal(c)}
          onOpenAnalytics={() => { setShowProfile(false); setShowAnalytics(true); setAnalyticsFrom('profile') }}
          onUnarchive={async (c) => {
            try {
              await updateCandidate(c.id, { archived: false, archive_reason: null })
              addLog('Восстановлен: ' + c.name, userProfile?.full_name)
              const fresh = await fetchActiveCandidates()
              setCandidates(fresh)
            } catch (e) { alert('Ошибка: ' + e.message) }
          }}
        />
      )}
{detailModal && <DetailModal
        candidate={detailModal}
        location={locations.find(l => l.id === detailModal.location_id)}
        statusColors={STATUS_COLORS}
        canEdit={isAdmin || (isOperator && detailModal.responsible_id === session?.user?.id)}
        canDelete={isAdmin || (isOperator && detailModal.responsible_id === session?.user?.id)}
        isMoscowOperator={isMoscowOperator}
        onEdit={() => { setCandidateModal({ mode: 'edit', data: detailModal }); setDetailModal(null) }}
        onDelete={() => handleDeleteCandidate(detailModal.id)}
        onArchive={() => handleArchiveCandidate(detailModal.id)}
        onMoveToSpecial={['Никита','Леонид','Анна'].includes(userProfile?.full_name) && !IS_SPECIAL ? async () => {
          if (!window.confirm('Переместить кандидата в Особую ЦРМ? Он исчезнет из основной.')) return
          await updateCandidate(detailModal.id, { workspace: 'special' })
          setCandidates(prev => prev.filter(c => c.id !== detailModal.id))
          setDetailModal(null)
        } : null}
        onMoveToMain={IS_SPECIAL ? async () => {
          if (!window.confirm('Вернуть кандидата в основную ЦРМ? Он исчезнет из особой.')) return
          await updateCandidate(detailModal.id, { workspace: 'main' })
          setCandidates(prev => prev.filter(c => c.id !== detailModal.id))
          setDetailModal(null)
        } : null}
        onClose={() => { setDetailModal(null); setDetailModalTab('details') }}
        currentUserId={session?.user?.id}
        isAdmin={isAdmin}
        isOperator={isOperator}
        currentUser={{ id: session?.user?.id, full_name: userProfile?.full_name || '', role }}
        onBack={backToFlightCard ? () => { setDetailModal(null); setDetailModalTab('details'); setReopenFlightCard(backToFlightCard); setBackToFlightCard(null) } : null}
        initialTab={detailModalTab}
        locations={locations}
        agents={agents}
        onUpdateCandidate={async (id, updates) => {
          await updateCandidate(id, updates)
          setCandidates(prev => prev.map(c => c.id === id ? { ...c, ...updates } : c))
          setDetailModal(prev => prev?.id === id ? { ...prev, ...updates } : prev)
        }}
      />}
</div>
  )
}